import { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TerminalContext, TerminalContextInterface } from '../../TerminalInit';
import { useSlot, useSlots } from '../../api/slots';
import { useSpotLayoutItems } from '../../api/spotLayoutItems';
import { useSpotLayout } from '../../api/spotLayouts';
import { useSpot } from '../../api/spots';
import { useMutateCreateTransaction, useMutateUpdateTransaction, useTransaction } from '../../api/transactions';
import { Transaction, UpdateTransactionStatus } from '../../common/transactions';
import ActionCard from '../../components/domain/ActionCard';
import { AllowedActionViewProps } from '../../components/domain/AllowedAction';
import useActivity from '../../hooks/useActivity';
import useScannerValue from '../../hooks/useScannerValue';
import { Logger } from '../../logs/Logger';
import { changeAllowRedirect } from '../../redux/ViewSlice';
import { getRandomString } from '../../utils';
import BackAndHomeNavigationButtons from '../../views/common/BackAndHomeNavigationButtons';
import BaseView from '../../views/common/BaseView';
import InfoView from '../../views/common/InfoView';
import LoadingView from '../../views/common/LoadingView';
import JustDrop from '../JustDrop';
import { useChangeDefaultScanner } from '../lookup/DefaultScannerContext';

type IMECFABDropoffActionProps = AllowedActionViewProps & {
    dispatch: any;
    onSelect: (action: ActionCard) => void;
    scannedValue?: string;
};

interface UserInputData {
    receiver_emails: string[];
    shipping_notes?: string;
    overwrite_tt_nr?: string;
}

const IMECFABDropoffView = (props: IMECFABDropoffActionProps) => {
    const intl = useIntl();
    const [, activity] = useActivity();
    const { terminal } = useContext<TerminalContextInterface>(TerminalContext);
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url);
    const { data: spotLayoutItems } = useSpotLayoutItems({ spotLayout: spotLayout }, { enabled: !!spotLayout });
    const { data: spot } = useSpot(spotLayout?.spot_url);
    const [error, changeError] = useState<string | null>(null);
    const [transactionId, changeTransactionId] = useState<string | null>(null);
    const [transaction, changeTransaction] = useState<Transaction | null>(null);
    const refetchedTransaction = useTransaction(undefined, transactionId ?? undefined, !!transactionId && !transaction?.slot_id);
    const { data: slot } = useSlot(transaction?.slot_id);
    const { data: fabSlots } = useSlots({ spot: spot?.id, fab: 'true', status: 'available', transaction_type: 'pudo' }, { enabled: !!spot });

    const [scannedValue, setScannedValue] = useState<string | undefined>(props.scannedValue);
    const [userInputData, setUserInputData] = useState<UserInputData>({ receiver_emails: ['@imec.be'] });
    const [submitted, setSubmitted] = useState<boolean>(false);

    const cancelTransaction = useMutateUpdateTransaction({});
    const updatedTransaction = useTransaction(undefined, transactionId ?? undefined, !!transactionId);
    const leaving = () => {
        if (updatedTransaction && updatedTransaction.status === 'TRS000002') {
            Logger.log('IMECFABDropoffView: Cancelling uncompleted transaction');
            cancelTransaction.mutate({
                transaction: updatedTransaction,
                updateTransaction: {
                    status: UpdateTransactionStatus.CANCELLED,
                },
            });
        }
    };
    const onBack = () => {
        leaving();
        props.onBack && props.onBack();
    };
    const onHome = () => {
        leaving();
        props.onHome && props.onHome();
    };
    const onInactivity = () => {
        leaving();
        props.onInactivity && props.onInactivity();
    };

    // Disable the default scanner
    useChangeDefaultScanner(false);

    const backHomeNav = (
        <BackAndHomeNavigationButtons
            onHome={onHome}
            onBack={onBack}
        />
    );

    const mutateCreateTransaction = useMutateCreateTransaction({
        onSuccess: (transaction) => {
            changeTransactionId(transaction.id);
        },
        onError: (error) => {
            if (error && JSON.stringify(error).includes('The tracking number is reserved')) {
                changeError(
                    intl.formatMessage({
                        id: 'workflow.AuthorisedContactWorkflow.IMECFABDropoff.trackingNumberReservedError',
                        defaultMessage: 'This tracking number is already reserved. Please use another one.',
                        description: 'Error message when the tracking number is already reserved for FAB dropoff',
                    })
                );
            } else
                changeError(
                    intl.formatMessage({
                        id: 'workflow.AuthorisedContactWorkflow.IMECFABDropoff.noSlotsAvailableError',
                        description: 'Error message when no slots are available for FAB dropoff',
                        defaultMessage: 'No more slots available for FAB dropoff. Please try again later.',
                    })
                );
        },
    });

    useScannerValue((value) => {
        if (scannedValue === undefined) {
            if (value && value.length > 0 && value.split(';').length === 3 && value.includes('@imec.be')) {
                setScannedValue(value);
            } else setScannedValue(undefined);
        }
    });

    useEffect(() => {
        activity();
    }, [userInputData]);

    const onSubmit = (data: UserInputData) => {
        if (!spot || data.receiver_emails.length === 0) return;
        setSubmitted(true);
        mutateCreateTransaction.mutate({
            createTransaction: {
                account_id: spot.account,
                spot_id: spot.id,
                tt_number: data.overwrite_tt_nr || getRandomString(20),
                sender_id: props.contact?.id,
                receiver_emails: data.receiver_emails,
                shipping_notes: data.shipping_notes,
                fab: true,
            },
        });
    };

    useEffect(() => {
        if (scannedValue) {
            const parts = scannedValue.split(';');
            if (parts.length === 3 && parts[1].includes('@imec.be')) {
                setUserInputData((prev) => ({
                    ...prev,
                    overwrite_tt_nr: parts[0],
                    receiver_emails: parts.filter((p) => p.includes('@imec.be')),
                }));
                onSubmit({
                    ...userInputData,
                    overwrite_tt_nr: parts[0],
                    receiver_emails: parts.filter((p) => p.includes('@imec.be')),
                });
            }
        }
    }, [scannedValue]);

    useEffect(() => {
        if (refetchedTransaction && refetchedTransaction.slot_id) {
            changeTransaction(refetchedTransaction);
        }
    }, [refetchedTransaction]);

    if (error)
        return (
            <InfoView
                navbarItems={backHomeNav}
                onInactivity={onInactivity}
                title={error}
            />
        );

    if (scannedValue && transaction === null) {
        return (
            <LoadingView
                onInactivity={onInactivity}
                onHome={onHome}
                onBack={onBack}
            />
        );
    }

    if (scannedValue === undefined && !submitted) {
        return (
            <BaseView
                onInactivity={onInactivity}
                navbarItems={backHomeNav}>
                <div className='d-flex w-100 h-100 flex-column justify-content-between'>
                    <div>
                        {userInputData.receiver_emails.map((email, index) => (
                            <div
                                className='input-group justify-content-between'
                                key={index}>
                                <div className='d-flex flex-column mb-3 w-100'>
                                    <label
                                        htmlFor={'receiver-email-' + (index + 1)}
                                        className='required'>
                                        <FormattedMessage
                                            id='views.IMECFAB.DROPOFF.ReceiverEmailLabel'
                                            description='The receiver email label on the IMEC FAB dropoff form view'
                                            defaultMessage='Receiver Email'
                                        />{' '}
                                        {index + 1}
                                    </label>
                                    <div
                                        id={'receiver-email-' + (index + 1)}
                                        className='d-flex flex-row justify-content-between align-items-center'>
                                        <span className='d-flex flex-row justify-content-start align-items-center w-100 pe-3'>
                                            <input
                                                className='form-control'
                                                type='text'
                                                autoComplete='off'
                                                required
                                                value={email.split('@')[0]}
                                                onChange={(event) =>
                                                    setUserInputData((prev) => {
                                                        const updatedEmails = [...prev.receiver_emails];
                                                        updatedEmails[index] = event.target.value + '@imec.be';
                                                        return { receiver_emails: updatedEmails };
                                                    })
                                                }
                                            />
                                            <span className='ms-2'>@imec.be</span>
                                        </span>
                                        {index === userInputData.receiver_emails.length - 1 && (
                                            <span className='d-flex flex-row align-items-center'>
                                                <button
                                                    className='btn btn-outline-primary'
                                                    onClick={() => setUserInputData({ receiver_emails: [...userInputData.receiver_emails, '@imec.be'] })}>
                                                    +
                                                </button>
                                                {userInputData.receiver_emails.length > 1 && (
                                                    <button
                                                        className='ms-2 btn btn-outline-danger'
                                                        onClick={() =>
                                                            setUserInputData({ receiver_emails: userInputData.receiver_emails.filter((_, i) => i !== index) })
                                                        }>
                                                        -
                                                    </button>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className='d-flex flex-column mb-3 w-100'>
                            <label
                                htmlFor='shipping-notes'
                                className='required'>
                                <FormattedMessage
                                    id='views.IMECFAB.DROPOFF.ShippingNotesLabel'
                                    description='The shipping notes label on the IMEC FAB dropoff form view'
                                    defaultMessage='Shipping Notes'
                                />
                            </label>
                            <div
                                id='shipping-notes'
                                className='d-flex flex-row justify-content-between align-items-center'>
                                <span className='d-flex flex-row justify-content-start align-items-center w-100 pe-3'>
                                    <textarea
                                        className='form-control'
                                        autoComplete='off'
                                        required
                                        value={userInputData.shipping_notes}
                                        onChange={(event) =>
                                            setUserInputData((prev) => ({
                                                ...prev,
                                                shipping_notes: event.target.value,
                                            }))
                                        }
                                    />
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        className='btn btn-primary'
                        onClick={(e) => {
                            e.preventDefault();
                            onSubmit(userInputData);
                        }}>
                        <FormattedMessage
                            id='WarehouseDropoff.submitButton'
                            description='The submitButton on the warehouse workflow dropoff page.'
                            defaultMessage='Submit'
                        />
                    </button>
                </div>
            </BaseView>
        );
    }

    if (!transaction || !transaction.slot_id || !slot) {
        return (
            <LoadingView
                onInactivity={onInactivity}
                onHome={onHome}
                onBack={onBack}
            />
        );
    }

    return (
        <JustDrop
            onInactivity={onInactivity}
            onHome={onHome}
            onBack={onBack}
            transaction={transaction}
            senderData={{
                senderContact: props.contact,
            }}
            receiverData={{}}
            slot={slot}
            transactionType={transaction.type}
            fab={true}
        />
    );
};

function IMECFABDropoff(props: IMECFABDropoffActionProps) {
    const intl = useIntl();
    const [scannedValue, setScannedValue] = useState<string | undefined>(undefined);
    const inValid = props.contact === undefined || !props.contact.fab;

    const imecFABDropoffAction = {
        id: 'workflow.IMECFABWorkflow.dropoffButton',
        actionText:
            'FAB ' +
            intl.formatMessage({
                id: 'workflow.PuDoWorkflow.dropoffButton',
                description: 'This is the dropoff action on PuDo',
                defaultMessage: 'Dropoff',
            }),
        highlight: true,
        icon: <i className='bi bi-box-arrow-in-down dashboard-action-icon' />,
        view: (
            <IMECFABDropoffView
                {...props}
                scannedValue={scannedValue}
            />
        ),
    };

    useEffect(() => {
        if (!inValid && scannedValue) {
            props.dispatch(changeAllowRedirect({ views: [], value: false }));
            props.onSelect(imecFABDropoffAction);
        }
    }, [scannedValue]);

    useScannerValue((value) => {
        if (!inValid) {
            if (value && value.length > 0 && value.split(';').length === 3 && value.includes('@imec.be')) {
                setScannedValue(value);
            } else setScannedValue(undefined);
        }
    });

    if (inValid) return undefined;

    return imecFABDropoffAction;
}

export default IMECFABDropoff;
